import { createStyles, makeUiStyles, Theme } from '../styles/styles';
import { fontDisplay, fontText, isMobileApp, StylesIndexInjections } from 'common-react';
import { focusStyle } from '../styles/mixins';
import { zIndex } from '../theme/constants';
import { ModalType } from './tools/ModalCore.const';
import { ModalStyleProps } from './tools/ModalCore.model';

const { modalZIndex } = zIndex;


export default makeUiStyles((theme: Theme) => createStyles({
  root: {
    padding: () => {
      const verticalPadding = 16;
      const mobileHeaderMinHeightOffset = isMobileApp() ? 56 : 0;
      return `${verticalPadding + theme.space.mobileTopPadding()}px 16px ${theme.space.mobileBottomPadding() + mobileHeaderMinHeightOffset + verticalPadding}px 16px`;
    },
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    width: 'auto',
    height: '100%',
    overflowY: 'auto',
    maxWidth: '100%',
    backgroundColor: theme.color.greys[0],
    zIndex: ({ customZIndex }: ModalStyleProps) => customZIndex ?? modalZIndex,
    boxShadow: '-5px 0 15px rgba(0, 0, 0, 0.1)',

    [theme.breakpoints.down('sm')]: {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      overflowY: 'scroll',
      padding: ({ type }: ModalStyleProps) => type === ModalType.menuModal ? 0 : '',
    },

    [theme.breakpoints.up('md')]: {
      padding: ({ type }) => type === ModalType.modalCard ? 0 : '24px 28px',
    },

    [theme.breakpoints.up('sm')]: {
      padding: ({ type }) => type === ModalType.modalCard ? '' : '24px 28px 56px',
    },

    // Conditional styling for mobile app
    ...(({ isMobileApp }: ModalStyleProps) => isMobileApp && {
      '-webkit-overflow-scrolling': 'touch',
      '-ms-overflow-style': 'none',
      scrollbarWidth: 'none',

      '&::-webkit-scrollbar': {
        display: 'none',
      },
    }),
  },

  goBackButton: {
    'button&': {
      position: 'relative',
      paddingLeft: 22,
      marginBottom: 16,
    },
  },

  closeButton: {
    position: 'absolute',
    padding: 0,
    top: '50%',
    right: 16,
    border: 'none',
    backgroundColor: 'transparent',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    '&:focus-visible': {
      ...focusStyle(theme),
    },
    '& svg': {
      verticalAlign: 'top',
      width: 16,
      height: 16,
      '& path': {
        fill: theme.color.greys[80]
      }
    },
  },

  header: {
    position: 'relative',
    margin: `-16px -${theme.space.appPadding}px 0px`,
    [theme.breakpoints.up('md')]: {
      margin: `-24px -28px 20px`,
    },
    display: 'flex',
    alignItems: 'center',
    justifyContent: ({ titleType }: ModalStyleProps) => titleType === 'center' ? 'center' : 'flex-start',
  },
  headerWithButtons: {
    position: 'relative',
    marginBottom: 20,
  },
  buttonsWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 0,
    '& $closeButton': {
      padding: 0,
      top: 0,
      right: 16,
      transform: 'none',
      cursor: 'pointer'
    }
  },
  titleWithButtons: {
    font: fontDisplay('normal', theme.fontSize.subHeadline2, 18),
  },
  menuModal: {
    position: 'relative',
    marginBottom: 0,
    padding: 16,
  },
  title: {
    color: theme.color.greys[80],
  },
  'title--center': {
    margin: '16px 0',
    font: fontText('normal', theme.fontSize.base, 20),
  },
  'title--left': {
    margin: 16,
    font: fontDisplay('normal', theme.fontSize.xl, 24),
    [theme.breakpoints.up('sm')]: {
      margin: '16px 28px',
      font: fontDisplay('normal', theme.fontSize.xxl, 32)
    },
  },
  modalWithLayout: {
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: ({ safeViewTopHeight }: ModalStyleProps) => safeViewTopHeight ?? 0,

    },
  },
  layout: {
    background: theme.color.greys[10],
    maxWidth: 'initial',
  },
  modalContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    width: 'auto',
    height: '100%',
    zIndex: ({ customZIndex }: ModalStyleProps) => customZIndex ?? modalZIndex,
    overflow: 'hidden',
  },
  modalBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'transparent',
    zIndex: -1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '0 24px',
  },
}), { name: 'ModalCore', index: StylesIndexInjections.UI_MOLECULES });
